/** Посилання лівого меню модуля поза групами типів записів: сторінки модуля з іконками, «Налаштування», «Проєктування». */
import { routes } from '@platform/core/lib/routes';
import type { IDesignEntity, IModuleDefinition, IModulePage, IModuleStrings, IRecordTypeDefinition } from '@platform/core/types';
import { isCatalogueEntity } from './designEntityRules';
import type { IMenuLink } from './IMenuLink';
import { LAW_MODEL_ICON } from './lawModelIcon';
import { LAW_MODEL_PAGE } from './lawModelLink';
import { modulePagesOf, pageLabel } from './modulePages';
import { customTabViewsOf } from '../list/filters/tabViews';
import { designItems, isDesigned, isHiddenType, kindOf, mainRecordTypeOf, settingsMenuItems, tableEntryKeyOf } from './moduleSections';

/**
 * Іконки сторінок як у прототипі v4: огляд, поради, аналітика, календар партії, довідка, візуальна модель закону (власний гліф) й аналітичні
 * подання (insight) за ключем сторінки.
 */
const PAGE_ICONS: Record<string, string> = {
  overview: 'layout-dashboard', advisory: 'lightbulb', analytics: 'chart-line', help: 'circle-help', pcal: 'calendar', [LAW_MODEL_PAGE]: LAW_MODEL_ICON,
  hall: 'landmark', icover: 'lightbulb', tradar: 'radar', fishbone: 'fish', library: 'library',
  cmatrix: 'grid-3x3', riskmatrix: 'grid-3x3', pcover: 'grid-3x3', fmatrix: 'grid-3x3', tmatrix: 'grid-3x3', swot: 'grid-3x3', tows: 'grid-3x3',
};

/** Іконка Lucide сторінки модуля; аналітичне подання без власної іконки (за напрямами, навантаження, трасування…) — лінійний графік. */
export function pageIconOf(key: string): string {
  return PAGE_ICONS[key] ?? 'chart-line';
}

/**
 * Порядок сторінок у меню (прототип v4): огляд, поради, аналітика, календар партії, далі аналітичні подання в порядку визначення, довідка,
 * «Візуальна модель закону» — остання.
 */
export function menuPageOrder(pages: IModulePage[]): IModulePage[] {
  const rank = (p: IModulePage): number => (p.key === LAW_MODEL_PAGE ? 3 : p.key === 'help' ? 2 : p.kind === 'insight' ? 1 : 0);
  return [...pages].sort((a, b) => rank(a) - rank(b));
}

/**
 * Посилання сторінок модуля з іконками; аналітика й аналітичні подання — звичайною вагою підпису. Власне подання записів, що є вкладкою списку
 * («Бібліотека» бази знань), веде на список у цьому поданні — та сама сторінка, що й вкладка (прототип v4: views[модуль] = library).
 */
export function pageLinksOf(
  moduleKey: string, definition: IModuleDefinition, strings: IModuleStrings, platformLabel: (key: string) => string,
): IMenuLink[] {
  return menuPageOrder(modulePagesOf(definition)).map((p) => {
    const rt = p.kind === 'insight' ? definition.recordTypes.find((r) => r.key === p.recordType) : undefined;
    const tab = rt && customTabViewsOf(rt).includes(p.key);
    return {
      key: p.key, label: pageLabel(p, strings, platformLabel), to: tab ? routes.list(moduleKey, rt.key, p.key) : routes.modulePage(moduleKey, p.key),
      icon: pageIconOf(p.key), plain: p.kind === 'insight' || p.key === 'analytics',
    };
  });
}

/**
 * Посилання розділу «Налаштування»: «Поля й подання», «Форма», «Записи й форми» (підписи — i18n рушія `settings.<ключ>`); на дочірній
 * таблиці (`type`, окремий модуль прототипу v4: «Видатки») — налаштування цієї таблиці (`?type=`).
 */
export function settingsLinksOf(moduleKey: string, label: (key: string) => string, type?: string): IMenuLink[] {
  return settingsMenuItems.map((k) => ({ key: k, label: label(k), to: routes.moduleSettings(moduleKey, k, undefined, type) }));
}

/** «Усі записи» реалізованого типу: список із пунктом меню таблиці (у поданні «Таблиця», якщо меню типу має такий пункт). */
function tableLinkOf(moduleKey: string, rt: IRecordTypeDefinition): string {
  const item = tableEntryKeyOf(rt);
  return routes.list(moduleKey, rt.key, item === 'view:table' ? 'table' : undefined, item);
}

/**
 * Довідники модуля (статуси, типи, теми, категорії…) — приглушені пункти в кінці «Налаштувань», як класифікатори прототипу v4 (setCls):
 * сутності-класифікатори головного типу (`designEntities`, isCatalogueEntity, крім `menu: false` — як isMod прототипу; підпис — `entityLabel`),
 * без них — типи-довідники визначення; спроєктований довідник відкриває «Записи й форми» з підсвіченим типом (прототип goDesign → cmFocus),
 * реалізований — «Усі записи» свого типу.
 */
export function catalogueLinksOf(
  moduleKey: string, definition: IModuleDefinition, strings: IModuleStrings, entityLabel?: (rtKey: string, index: number, entity: IDesignEntity) => string,
): IMenuLink[] {
  const main = mainRecordTypeOf(definition);
  if (main?.designEntities && entityLabel) {
    return main.designEntities.flatMap((e, i): IMenuLink[] => {
      const rt = e.recordType ? definition.recordTypes.find((r) => r.key === e.recordType) : undefined;
      if (e.menu === false || !isCatalogueEntity(e) || (rt && isHiddenType(rt))) return [];
      const to = rt && !isDesigned(rt) ? tableLinkOf(moduleKey, rt) : routes.moduleSettings(moduleKey, 'records', e.recordType ?? e.name.en ?? '');
      return [{ key: `cat:${i}`, label: entityLabel(main.key, i, e), to, muted: true }];
    });
  }
  return definition.recordTypes.filter((rt) => kindOf(rt) === 'catalogue' && !isHiddenType(rt)).map((rt) => {
    const to = isDesigned(rt) ? routes.moduleSettings(moduleKey, 'records', rt.key) : tableLinkOf(moduleKey, rt);
    return { key: `rt:${rt.key}`, label: strings.entity(rt.key, 'name', rt.name), to, muted: true };
  });
}

/**
 * Посилання розділу «Проєктування» (прототип v4 mdItems) з лічильниками `counts` за ключем розділу — записи бізнес-аналізу модуля за видом,
 * шляхи користувачів, типи записів (useDesignCounts); без лічильника (ще вантажиться) — порожньо.
 */
export function designLinksOf(moduleKey: string, counts: Partial<Record<string, number | undefined>>, label: (key: string) => string): IMenuLink[] {
  return designItems.map((k) => ({ key: k, label: label(k), to: routes.moduleDesign(moduleKey, k), count: counts[k] }));
}
